import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Mail, BellRing, Clock } from "lucide-react";

export function NotificationSettingsDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState(user?.notif_email ?? true);
  const [inapp, setInapp] = useState(user?.notif_inapp ?? true);
  const [reminderTime, setReminderTime] = useState(user?.reminder_time ?? "evening");

  const save = async (override = {}) => {
    setSaving(true);
    try {
      const payload = {
        notif_email: email, notif_inapp: inapp, reminder_time: reminderTime, ...override,
      };
      const { data } = await api.put("/preferences", payload);
      setUser(data.user);
      toast.success("Preferenze aggiornate");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="notification-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Preferenze notifiche</DialogTitle>
          <DialogDescription>
            Attiva o disattiva in modo indipendente i canali di notifica.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-blue-700" />
              <div>
                <p className="text-sm font-600 text-slate-900">Email</p>
                <p className="text-xs text-slate-500">Ricevi aggiornamenti via email</p>
              </div>
            </div>
            <Switch
              data-testid="pref-email-switch"
              checked={email}
              disabled={saving}
              onCheckedChange={(v) => { setEmail(v); save({ notif_email: v }); }}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <BellRing className="h-5 w-5 text-blue-700" />
              <div>
                <p className="text-sm font-600 text-slate-900">In-App</p>
                <p className="text-xs text-slate-500">Notifiche nella campanella</p>
              </div>
            </div>
            <Switch
              data-testid="pref-inapp-switch"
              checked={inapp}
              disabled={saving}
              onCheckedChange={(v) => { setInapp(v); save({ notif_inapp: v }); }}
            />
          </div>
          {user?.role === "isf" && (
            <div className="rounded-lg border border-slate-200 p-4" data-testid="reminder-time-section">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-blue-700" />
                <div>
                  <p className="text-sm font-600 text-slate-900">Promemoria visita</p>
                  <p className="text-xs text-slate-500">Quando ricevere il promemoria dell'appuntamento</p>
                </div>
              </div>
              <Select value={reminderTime} disabled={saving}
                onValueChange={(v) => { setReminderTime(v); save({ reminder_time: v }); }}>
                <SelectTrigger className="mt-3" data-testid="reminder-time-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="evening">La sera prima (ore 18:00)</SelectItem>
                  <SelectItem value="morning">La mattina stessa (ore 07:00)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
